import { SimpleTooltip } from '../../tooltip';
import { Checkbox, CircularProgress } from '@mui/material';
import { LocalDateRow, TextRow } from '../tableRows';
import { FindLoad, Images, previewFile, tagEnums } from '../../../utils';
import React from 'react';
import moment from 'moment';
import { FilterTags, TagComponent } from '../../tagFiltering';
import { EditWithIcon } from '../../buttons';
import { canReadTags } from 'store/tags/tag.utils';

export const fileTabEnums = {
    ACTIVE: 'ACTIVE',
    ARCHIVED: 'ARCHIVED',
};
export const fileTabTypes = [
    { title: 'Active Files', tabValue: fileTabEnums.ACTIVE },
    { title: 'Archived Files', tabValue: fileTabEnums.ARCHIVED },
];

/** Same window as the dashboard "Client Document Alerts" (DASHBOARD_EXPIRING_SOON_DAYS in the API). */
const EXPIRING_SOON_DAYS = 14;

/** Expiration date (a date, not a time — shown as stored) with an Expired / Expiring soon badge. */
const ExpirationCell = ({ date, archived }) => {
    if (!date) return <TextRow name={'--'} />;
    const label = moment.utc(date).format('MM/DD/YYYY');
    const daysLeft = moment.utc(date).startOf('day').diff(moment().utc().startOf('day'), 'days');
    const status = archived ? null : daysLeft < 0 ? 'expired' : daysLeft <= EXPIRING_SOON_DAYS ? 'expiring' : null;
    return (
        <div className="flex-align-center" style={{ gap: 6, whiteSpace: 'nowrap' }}>
            <span>{label}</span>
            {status && (
                <span
                    style={{
                        background: status === 'expired' ? '#FEF3F2' : '#FFFAEB',
                        color: status === 'expired' ? '#B42318' : '#B54708',
                        borderRadius: 16,
                        padding: '2px 8px',
                        fontSize: 12,
                        fontWeight: 600,
                    }}
                >
                    {status === 'expired' ? 'Expired' : 'Expiring soon'}
                </span>
            )}
        </div>
    );
};

export const filesHead = (checkPermissions, noSearch, selectList, fullList, handleSelect, withExpiration) => {
    return [
        checkPermissions && {
            renderView: (
                <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                    <Checkbox
                        onClick={(e) => handleSelect(e, '', 'all')}
                        name={'checkBox'}
                        style={{ padding: 0, color: '#0C66E4' }}
                        checked={fullList?.length === selectList?.length}
                    />
                </SimpleTooltip>
            ),
            width: '30px',
        },
        { name: 'name', title: 'Name', custom: false, noSearch: noSearch },
        { name: 'description', title: 'Description', custom: false, noSearch: noSearch },
        { name: 'updatedBy', title: 'Uploaded By', custom: false, noSearch: noSearch },
        canReadTags() && { name: '', title: 'Tags', custom: false, noSearch: noSearch },
        // { name: '', title: 'Tags', custom: false, renderView: <FilterTags /> },
        { name: 'date', title: 'Date Uploaded', custom: false, disabled: true, noSearch: noSearch },
        withExpiration && { name: 'expirationDate', title: 'Expires', custom: false, disabled: true, noSearch: noSearch },
        { name: 'file', rowName: 'format', title: 'File Type', custom: false, width: '150px', noSearch: noSearch },
        { name: '', title: 'Action', custom: false, disabled: true, width: '230px', noSearch: noSearch },
    ];
};

const renderFileTypeAndColor = (file, archived) => {
    // const background =
    // file === 'svg+xml' ? '#FFCA33' :
    //     file === 'jpeg' ? '#51439E' :
    //         file === 'jpg' ? '#51439E' :
    //             file === 'png' ? '#155EEF' :
    //                 file === 'pdf' ? '#CC400C' :
    //                     '#FFCA33';

    const image = archived
        ? file === 'svg+xml'
            ? Images.archivedSvg
            : file === 'jpeg'
              ? Images.archivedJpeg
              : file === 'jpg'
                ? Images.archivedJpg
                : file === 'png'
                  ? Images.archivedPng
                  : file === 'pdf'
                    ? Images.archivedPdf
                    : null
        : file === 'svg+xml'
          ? Images.svgFile
          : file === 'jpeg'
            ? Images.jpeg
            : file === 'jpg'
              ? Images.jpg
              : file === 'png'
                ? Images.png
                : file === 'pdf'
                  ? Images.pdf
                  : null;

    const text =
        file === 'svg+xml'
            ? 'SVG'
            : file === 'jpeg'
              ? 'JPEG'
              : file === 'jpg'
                ? 'JPG'
                : file === 'png'
                  ? 'PNG'
                  : file === 'pdf'
                    ? 'PDF'
                    : file;

    return (
        <div>
            {image ? (
                <img src={image} alt="fileType" />
            ) : (
                <div className="file-type-box" style={{ background: archived ? '#EDF1F7' : '#155EEF' }}>
                    {text}
                </div>
            )}
        </div>
    );
};

export const documentActions = {
    archive: 'archive',
    download: 'download',
};
export const filesBody = (checkPermissions, selectedId, selectedList, handleSelect, requestParams, archived, shareBtn, withExpiration) => {
    const loadArchive = FindLoad('ARCHIVE_FILES');

    return [
        checkPermissions && {
            button: (handle, item) => {
                return (
                    <Checkbox
                        onClick={(e) => handleSelect(e, item)}
                        name={'checkBox'}
                        style={{ padding: 0, color: '#0C66E4' }}
                        checked={selectedList?.includes(item?.id)}
                    />
                );
            },
            notClickable: true,
        },
        {
            rowText: (item) => <TextRow name={item?.name} textWidth={9} />,
        },
        {
            rowText: (item) => <TextRow name={item?.description} textWidth={9} />,
        },
        {
            rowText: (item) => <TextRow name={item?.uploadedBy?.fullName ? item?.uploadedBy?.fullName : ''} textWidth={9} />,
        },
        canReadTags() && {
            rowText: (item) => (
                <TagComponent
                    userInfo={item}
                    module={tagEnums.DOCUMENT}
                    requestParams={requestParams}
                    assignParams={{
                        resourceModel: requestParams?.model,
                        resourceId: requestParams?.id,
                    }}
                />
            ),
            notClickable: true,
        },
        {
            // rowText: (item) => <DateRow date={item?.uploadedDate} />,
            rowText: (item) => <LocalDateRow date={item?.uploadedDate} />,
        },
        withExpiration && {
            rowText: (item) => <ExpirationCell date={item?.expirationDate} archived={archived} />,
        },
        {
            rowText: (item) => <div>{renderFileTypeAndColor(item?.file?.format, archived)}</div>,
        },
        {
            button: (fun, item) => (
                <div className={'document-actions'}>
                    <EditWithIcon
                        id={'show-btn'}
                        icon={Images.showEye}
                        handleClick={() => previewFile(item?.file)}
                    />
                    {
                        checkPermissions && <EditWithIcon id={'edit-btn'} icon={Images.edit} handleClick={() => fun('edit')} />
                    }
                    <EditWithIcon
                        load={selectedId?.action === documentActions.download && selectedId.id === item?.id}
                        id={'download-btn'}
                        icon={Images.downloadPdf}
                        handleClick={() => fun('download')}
                    />

                    {shareBtn && (
                        <EditWithIcon
                            id={'share-btn'}
                            icon={item?.sharedAt ? Images.unShare : Images.sendIcon}
                            handleClick={() => fun(item?.sharedAt ? 'unShare' : 'share')}
                        />
                    )}

                    {checkPermissions && (
                        <EditWithIcon
                            id={'archie-btn'}
                            load={loadArchive?.length && selectedId.id === item?.id}
                            icon={archived ? Images.unarchive : Images.archive}
                            handleClick={() => fun('archiveAnarchive')}
                        />
                    )}

                    {checkPermissions && <EditWithIcon id={'delete-btn'} icon={Images.trash} handleClick={() => fun('delete')} />}
                </div>
            ),
            notClickable: true,
        },
    ];
};

export const fileNotYet = {
    title: 'No Files Uploaded',
    subTitle: 'Upload files to store relevant documents here.',
    image: Images.notFile,
};
